import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { laneApi, TeamMember, LaneId } from '../../api/laneApi';
import { filtersFromSearchParams, applyFilter } from './laneUrlState';
import './laneshell-controls.css';

/**
 * Toolbar "assigned staff" filter -- a dropdown view onto the exact same
 * `assigned` URL param the omnibox's `assigned:<name|unassigned>` operator
 * writes (see parseOmniQuery.ts / Omnibox.tsx's `applyChip` case 'assigned'
 * and laneUrlState.applyFilter). Two views of one filter, not a parallel
 * concept:
 *   - "All staff"   -> clears the param (same as removing the omnibox chip).
 *   - a roster name -> `assigned=<exact TeamMember.name>`, matching what
 *     team_tasks.bulk_assign_tasks stores in `assignedToTeamMember` (the
 *     *name*, not the email -- task_lane_service._build_common_filters does
 *     a case-sensitive `assignedToTeamMember = :assigned` exact match, so
 *     the value written here MUST be the roster's `name` verbatim).
 *   - "Unassigned"  -> `assigned=unassigned`, the sentinel
 *     _build_common_filters lowercases and compares before falling back to
 *     the `(assignedToTeamMember IS NULL OR = '')` predicate.
 *
 * Because Omnibox reads `filters.assigned` from the same URL on every
 * render, selecting a value here already surfaces the existing removable
 * "Assigned: X" chip (and its archived-mode muting) with no duplicate chip
 * markup needed in this component.
 */

const UNASSIGNED_VALUE = 'unassigned';
const ARCHIVED_TOOLTIP = "Not applied -- archived browsing doesn't support an assignee filter.";

export interface AssignedFilterDropdownProps {
  /** Scopes the fetched roster's `pendingCount` to this lane (reviewer
   * blocker -- see MemberPickerModal's identical `lane` prop doc: without
   * it, the roster fetch behind this dropdown summed a member's pending
   * workload across all four lanes). This dropdown doesn't currently render
   * pendingCount itself, but shares the same `laneApi.getTeamMembers` roster
   * fetch as MemberPickerModal, so an unscoped call here still contributes
   * to the same cross-lane-total bug class if either component's fetch is
   * ever reused to drive a count display. LaneShell always supplies its own
   * `lane` prop straight through. */
  lane?: LaneId;
}

export const AssignedFilterDropdown: React.FC<AssignedFilterDropdownProps> = ({ lane }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = filtersFromSearchParams(searchParams);
  const [members, setMembers] = useState<TeamMember[]>([]);

  useEffect(() => {
    let cancelled = false;
    laneApi.getTeamMembers(lane)
      .then((roster) => { if (!cancelled) setMembers(roster); })
      .catch(() => {
        // Non-fatal: dropdown just falls back to All staff/Unassigned only.
      });
    return () => { cancelled = true; };
  }, [lane]);

  const currentValue = filters.assigned ?? '';

  // The <select>'s value must always be one of its <option> values or React
  // silently falls back to the first option (visually resetting to "All
  // staff" while the URL still has the real filter applied). Guards a name
  // that's in the URL (e.g. typed via the omnibox operator, or a deep link)
  // but isn't (yet) in the fetched roster by synthesising an option for it.
  const knownValues = new Set<string>([UNASSIGNED_VALUE, ...members.map((m) => m.name)]);
  const hasUnknownValue = currentValue !== '' && !knownValues.has(currentValue);

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setSearchParams((prev) => applyFilter(prev, 'assigned', value === '' ? null : value));
  };

  return (
    <div className="lane-shell__assigned-filter-wrap">
      <select
        className="lane-shell__assigned-filter"
        aria-label="Filter by assigned staff"
        value={currentValue}
        onChange={handleChange}
        disabled={filters.archived}
        title={filters.archived ? ARCHIVED_TOOLTIP : undefined}
      >
        <option value="">All staff</option>
        {hasUnknownValue && <option value={currentValue}>{currentValue}</option>}
        {members.map((m) => (
          <option key={m.email} value={m.name}>{m.name}</option>
        ))}
        <option value={UNASSIGNED_VALUE}>Unassigned</option>
      </select>
    </div>
  );
};

export default AssignedFilterDropdown;
